import {
  useAllConversationCountsQuery,
  useAvailableClientTagsQuery,
} from '@/generated/graphql';
import {useSubscribeToNotifications} from '@/hooks/useInitializeNotifications';
import {deriveActiveFilters} from '@/pages/ENT/Conversations/List/conversationFilterBlocks';
import {ConversationFilter} from '@/pages/ENT/Conversations/List/ConversationsPageContext';
import {edgesToNodes} from '@ark/react';
import {useUserProfile, useSelectedCommunities} from '@ark/web';
import {DateTime} from 'luxon';
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useSearchParams} from 'react-router-dom';

type ConversationCounts = Record<ConversationFilter, number>;

type ConversationCountsContextType = {
  counts: ConversationCounts;
  loading: boolean;
  dateFrom: DateTime | null;
  dateTo: DateTime | null;
  setDateFrom: (date: DateTime | null) => void;
  setDateTo: (date: DateTime | null) => void;
  selectedTags: string[];
  setSelectedTags: (tags: string[] | ((prev: string[]) => string[])) => void;
  selectedUsers: string[];
  setSelectedUsers: (users: string[] | ((prev: string[]) => string[])) => void;
};

const ConversationCountsContext = createContext<
  ConversationCountsContextType | undefined
>(undefined);

type ConversationCountsProviderProps = {
  children: ReactNode;
};

export const ConversationCountsProvider = ({
  children,
}: ConversationCountsProviderProps) => {
  const communities = useSelectedCommunities();
  const {id: userId, teamIds} = useUserProfile();
  const [searchParams] = useSearchParams();
  const participantTypeValue = useMemo(() => {
    const sender = deriveActiveFilters(searchParams.getAll('filter')).sender;
    return sender ? [sender] : undefined;
  }, [searchParams]);
  const [dateFrom, setDateFrom] = useState<DateTime | null>(null);
  const [dateTo, setDateTo] = useState<DateTime | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  const {data: clientTagsData} = useAvailableClientTagsQuery();
  const availableTags = useMemo(
    () => edgesToNodes(clientTagsData?.clientTags?.edges),
    [clientTagsData?.clientTags?.edges],
  );
  const hasInitializedTagsRef = useRef(false);

  if (
    availableTags.length > 0 &&
    selectedTags.length === 0 &&
    !hasInitializedTagsRef.current
  ) {
    hasInitializedTagsRef.current = true;
    setSelectedTags(['UNTAGGED', ...availableTags.map(tag => tag.id)]);
  }

  const clientTagsValue = useMemo(() => {
    if (selectedTags.length > 0) return selectedTags;
    return undefined;
  }, [selectedTags]);

  const assignedUsersValue = useMemo(() => {
    if (selectedUsers.length > 0) return selectedUsers;
    return undefined;
  }, [selectedUsers]);

  const {data, previousData, loading, refetch} = useAllConversationCountsQuery({
    variables: {
      communities,
      userId: userId || '',
      teamIds: teamIds?.length ? teamIds : [],
      dateFrom: dateFrom?.isValid ? dateFrom.toISODate() : undefined,
      dateTo: dateTo?.isValid ? dateTo.toISODate() : undefined,
      clientTags: clientTagsValue,
      assignedUsers: assignedUsersValue,
      participantType: participantTypeValue,
    },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  const countsData = data ?? previousData;

  useSubscribeToNotifications(notification => {
    if (
      notification?.target?.__typename === 'ConversationNode' ||
      notification?.target?.__typename === 'ConversationAssignmentNode'
    ) {
      refetch();
    }
  }, 'ConversationCounts');

  const value = useMemo(() => {
    const c = countsData?.conversationCounts;
    const counts: ConversationCounts = {
      all: c?.all ?? 0,
      needsResponse: c?.needsResponse ?? 0,
      snoozed: c?.snoozed ?? 0,
      assigned: c?.assigned ?? 0,
      assignedToMyTeam: c?.assignedToMyTeam ?? 0,
      unassigned: c?.unassigned ?? 0,
      fromEmail: c?.fromEmail ?? 0,
      fromArk: c?.fromArk ?? 0,
      closed: 0,
      inactive: c?.inactive ?? 0,
    };
    return {
      counts,
      loading,
      dateFrom,
      dateTo,
      setDateFrom,
      setDateTo,
      selectedTags,
      setSelectedTags,
      selectedUsers,
      setSelectedUsers,
    };
  }, [countsData, loading, dateFrom, dateTo, selectedTags, selectedUsers]);

  return (
    <ConversationCountsContext.Provider value={value}>
      {children}
    </ConversationCountsContext.Provider>
  );
};

export const useConversationCounts = () => {
  const context = useContext(ConversationCountsContext);
  if (!context) {
    throw new Error(
      'useConversationCounts must be used within a ConversationCountsProvider',
    );
  }
  return context;
};
